// Custom fields: the tenant's definitions, the admin writes, and setting a
// value on a ticket.  tickets[9399]
//
// Reads embed each field's options and departments, so one query gives every
// surface — settings, the ticket page, the list — the whole shape. Writes go
// straight to the tables (RLS: admins only); a field's departments are saved
// through set_custom_field_subsheets / set_subsheet_custom_fields, and a
// ticket's value through set_ticket_custom_field, which changes only that key
// (0064).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/db/client";
import type { Json } from "@/integrations/db/types.generated";
import { toCustomField, type CustomField, type CustomFieldType } from "@/lib/customFields";

export const CUSTOM_FIELDS_KEY = ["custom-fields"] as const;

/**
 * The tenant's fields with their options and departments. Kept fresh by
 * useGlobalCustomFieldsSync, mounted once in AppContent.
 */
export const useCustomFields = () =>
  useQuery<CustomField[]>({
    queryKey: CUSTOM_FIELDS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_fields")
        .select(
          "id, name, description, field_type, order_index, custom_field_options(id, label, color, order_index), custom_field_subsheets(subsheet_id)",
        )
        .order("order_index", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map(toCustomField);
    },
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });

/** A pick-list option as the editor holds it: no id yet for a new one. */
export interface OptionDraft {
  id?: string;
  label: string;
  color: string | null;
}

export interface CustomFieldDraft {
  /** Omitted to create a new field. */
  id?: string;
  name: string;
  description: string;
  /** Only read when creating: a field's type never changes. */
  field_type: CustomFieldType;
  /** Pick-lists only, in order. */
  options: OptionDraft[];
  /**
   * Only read when creating: the departments the new field starts in. After
   * that the Departments dialogs own them.
   */
  subsheetIds?: string[];
}

/** A field's own departments, saved whole through set_custom_field_subsheets (0064). */
const setFieldSubsheets = (fieldId: string, subsheetIds: string[]) =>
  supabase.rpc("set_custom_field_subsheets", { _field_id: fieldId, _subsheet_ids: subsheetIds });

export function useCustomFieldMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: CUSTOM_FIELDS_KEY });

  const save = useMutation({
    mutationFn: async ({ draft }: { draft: CustomFieldDraft }) => {
      const name = draft.name.trim();
      if (!name) throw new Error("A field needs a name");
      const description = draft.description.trim() || null;
      const labels = draft.options.map((o) => o.label.trim());
      if (draft.field_type === "select") {
        if (labels.length === 0) throw new Error("A pick-list needs at least one option");
        if (labels.some((l) => !l)) throw new Error("Every option needs a label");
        if (new Set(labels.map((l) => l.toLowerCase())).size !== labels.length)
          throw new Error("Two options have the same label");
      }

      let id = draft.id;
      if (id) {
        const { error } = await supabase.from("custom_fields").update({ name, description }).eq("id", id);
        if (error) throw error;
      } else {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        // Appended after the last one, so the up/down arrows always have two
        // distinct indexes to swap.
        const { data: last, error: lastError } = await supabase
          .from("custom_fields")
          .select("order_index")
          .order("order_index", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (lastError) throw lastError;
        const { data, error } = await supabase
          .from("custom_fields")
          .insert({
            name,
            description,
            field_type: draft.field_type,
            created_by_id: user.id,
            order_index: (last?.order_index ?? -1) + 1,
          })
          .select("id")
          .single();
        if (error) throw error;
        id = data.id;
      }

      // A new field that fails a later step would linger half-made and block a
      // retry under its name, so take it back out.
      const fail = async (error: { message: string }): Promise<never> => {
        if (!draft.id) {
          const undo = await supabase.from("custom_fields").delete().eq("id", id);
          if (undo.error) throw new Error(`${error.message} (and "${name}" was created anyway; delete it)`);
        }
        throw error;
      };

      // A new field's starting departments (#9764).
      if (!draft.id && draft.subsheetIds?.length) {
        const { error } = await setFieldSubsheets(id, draft.subsheetIds);
        if (error) await fail(error);
      }

      if (draft.field_type !== "select") return;

      // All options in one call (0064): removed ones are deleted (and
      // stripped from tickets), the rest updated or added in this order.
      const options = await supabase.rpc("set_custom_field_options", {
        _field_id: id,
        _options: draft.options.map((o) => ({ id: o.id ?? null, label: o.label.trim(), color: o.color })),
      });
      if (options.error) await fail(options.error);
    },
    onSuccess: (_data, { draft }) => {
      invalidate();
      toast.success(draft.id ? "Field updated" : "Field added");
    },
    onError: (e: Error) => toast.error(e.message || "Could not save the field"),
  });

  // Swap two fields' order_index to move one up or down, as statuses do.
  const swapOrder = useMutation({
    mutationFn: async ([a, b]: [CustomField, CustomField]) => {
      const results = await Promise.all([
        supabase.from("custom_fields").update({ order_index: b.order_index }).eq("id", a.id),
        supabase.from("custom_fields").update({ order_index: a.order_index }).eq("id", b.id),
      ]);
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message || "Could not reorder"),
  });

  // The database strips the field's values from every ticket (0064).
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("custom_fields").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
      toast.success("Field deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete the field"),
  });

  return { save, swapOrder, remove };
}

/** One department's own fields, as Departments -> Manage fields saves them. */
export interface DepartmentFieldsChange {
  subsheetId: string;
  fieldIds: string[];
}

/** Saves one department's own field list through set_subsheet_custom_fields. Callers say what saved. */
export function useSetDepartmentFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ subsheetId, fieldIds }: DepartmentFieldsChange) => {
      const { error } = await supabase.rpc("set_subsheet_custom_fields", {
        _subsheet_id: subsheetId,
        _field_ids: fieldIds,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOM_FIELDS_KEY }),
  });
}

/** A field's own departments, as the field-side dialog saves them. */
export interface FieldDepartmentsChange {
  fieldId: string;
  subsheetIds: string[];
}

/**
 * Saves a field's departments from the field's side through
 * set_custom_field_subsheets. Takes a list because the shared Departments
 * dialog hands its save a list of changes; this one only ever sends one.
 */
export function useSetFieldDepartments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: readonly FieldDepartmentsChange[]) => {
      for (const { fieldId, subsheetIds } of changes) {
        const { error } = await setFieldSubsheets(fieldId, subsheetIds);
        if (error) throw error;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOM_FIELDS_KEY }),
  });
}

/** How many live tickets hold a value for this field — for the delete confirmation. */
export async function countFieldValues(fieldId: string): Promise<number> {
  const { count, error } = await supabase
    .from("backlog_items")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .not(`custom_fields->${fieldId}`, "is", null);
  if (error) throw error;
  return count ?? 0;
}

/**
 * Sets one value on one ticket. Patches the cached list and ticket at once,
 * then settles on what the database stored (it drops empties and may refuse).
 */
export function useSetTicketCustomField() {
  const queryClient = useQueryClient();
  type Vars = { itemId: string; fieldId: string; value: unknown };
  const patch = (itemId: string, apply: (values: Record<string, unknown>) => Record<string, unknown>) => {
    const update = <T extends { id: string; custom_fields?: unknown }>(item: T): T =>
      item.id === itemId
        ? { ...item, custom_fields: apply({ ...((item.custom_fields as Record<string, unknown>) ?? {}) }) }
        : item;
    queryClient.setQueriesData<Array<{ id: string; custom_fields?: unknown }>>(
      { queryKey: ["backlog-items"] },
      (old) => (Array.isArray(old) ? old.map(update) : old),
    );
    queryClient.setQueriesData<{ id: string; custom_fields?: unknown } | null>({ queryKey: ["backlog-item"] }, (old) =>
      old && typeof old === "object" && "id" in old ? update(old) : old,
    );
  };

  return useMutation({
    mutationFn: async ({ itemId, fieldId, value }: Vars) => {
      const { data, error } = await supabase.rpc("set_ticket_custom_field", {
        _item_id: itemId,
        _field_id: fieldId,
        _value: (value ?? null) as Json,
      });
      if (error) throw error;
      return data as Record<string, unknown>;
    },
    onMutate: ({ itemId, fieldId, value }: Vars) =>
      patch(itemId, (values) => {
        if (value === null || value === undefined || value === "") delete values[fieldId];
        else values[fieldId] = value;
        return values;
      }),
    onSuccess: (stored, { itemId }) => patch(itemId, () => stored ?? {}),
    onError: (e: Error) => {
      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
      toast.error(e.message || "Could not save the field");
    },
  });
}
